import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CdkTableModule } from '@angular/cdk/table';
import { HeaderComponent } from '../shared/header.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { gameInfo } from '../core/game';
import { SessionDto } from '@oracychampions/api-interfaces';
import { Observable, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'oc-session-new',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CdkTableModule,
    HeaderComponent,
    ReactiveFormsModule,
    FormlyBootstrapModule,
    FormlyModule,
  ],
  template: `
    <div class="oc-session-new">
      <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
        <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
        <div class="d-flex gap-2">
          <button type="submit" class="btn btn-primary mt-3 mb-4">
            <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
            <span *ngIf="!saving">Start</span>
          </button>
        </div>
      </form>
    </div>
  `,
  styles: `
    $col: #13b8d8;

    .oc-session-new {
      padding-block: 2rem;

      .btn-primary {
        background-color: $col;
        border-color: $col;
      }
    }

    ::ng-deep {
      .form-label {
        font-size: 2rem;
        font-weight: 500;
      }
    }

    ::ng-deep .oc-game-students {
      .form-label {
        display: block;
      }

      .form-check {
        display: inline-block;
        width: 200px;
        height: 45px;
        margin-right: 1rem;
        padding: 0.5rem 0.5rem 0.5rem 2.5rem;
        background: rgba(255,255,255,0.3);
        border-radius: 0.5rem;
        position: relative;

        .form-check-input:checked {
          background-color: $col;
          border-color: $col;
          box-shadow: none;
        }

        label {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          line-height: 47px;
          padding-left: 43px;
          cursor: pointer;
        }
      }
    }

    ::ng-deep .oc-game-choose {
      .form-label {
        display: block;
      }

      .form-check:nth-child(3) {
        label {
          &::before {
            background-image: url('/VOICE-NOTE.png');
            background-size: cover;
          }
        }
      }

      .form-check:nth-child(4) {
        label {
          &::before {
            background-image: url('/YES-NO-GAME.png');
            background-size: cover;
          }
        }
      }

      .form-check:nth-child(5) {
        label {
          &::before {
            background-image: url('/DEBATE-GAME.png');
            
          }
        }
      }

      .form-check:nth-child(6) {
        label {
          &::before {
            background-image: url('/PICTURE-GAME.png');
            
          }
        }
      }
      
      .form-check {
        display: inline-block;
        width: 200px;
        margin-right: 1rem;
        padding: 200px 0.5rem 0.5rem 2.5rem;
        background: rgba(255,255,255,0.3);
        border-radius: 0.5rem;
        position: relative;

        .form-check-input {
          position: absolute;
          top: 0;
          left: 0;
          margin-left: 0.8rem;
          margin-top: 0.8rem;
          z-index: 1;
        }

        .form-check-input:checked {
          background-color: $col;
          border-color: $col;
          box-shadow: none;
        }

        label {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          padding: 172px 1rem 0.75rem 1rem;
          cursor: pointer;

          &::before {
            content: '';
            position: absolute;
            top: 5px;
            left: 5px;
            right: 5px;
            height: 158px;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            background: rgba(255,255,255,0.1);
            background-size: cover;
          }

          &::after {
            content: '';
            position: absolute;
            top: 5px;
            left: 5px;
            right: 5px;
            height: 158px;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            background: linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 45%);
          }
        }
      }
    }
  `,
})
export class SessionNewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  form = new FormGroup({});
  model = { game: 0, students: [] };

  @Input() sessionId: string;

  private games = gameInfo;

  fields: FormlyFieldConfig[];
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  saving = false;

  session$: Observable<SessionDto>;

  constructor() {
    this.session$ = this.route.parent!.data.pipe(map((data) => data['session']));
  }

  ngOnInit() {
    this.fields = [
      {
        key: 'game',
        type: 'radio',
        className: 'oc-game-choose',
        props: {
          label: 'Choose game',
          required: true,
          options: this.games,
          hideRequiredMarker: true,
        },
      },
      {
        key: 'students',
        type: 'multicheckbox',
        className: 'oc-game-students',
        props: {
          label: "Who's playing?",
          required: true,
          hideRequiredMarker: true,

          options: this.session$.pipe(
            map((session) => session.students.map((student) => ({ label: student.name, value: student.id }))),
          ),
        },
      },
    ];
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }
    if (!this.form.valid) {
      this.toastr.error('Please review and try again');
      return;
    }

    this.saving = true;
    const gameInfo = btoa(JSON.stringify(model));

    this.router.navigate(['session', this.sessionId, 'participate', gameInfo]);
  }
}
